import { useEffect, useRef, useCallback } from 'react'
import { MessageRenderer } from './MessageRenderer'

interface Message {
  id: string
  role: 'user' | 'assistant'
  content: string
  parts?: any[] // Parts for tool calls and other message components
  timestamp: Date
}

interface MessageListProps {
  messages: Message[]
  isLoading?: boolean
  onScrollToBottom?: (scrollElement: HTMLElement) => void
  projectIconUrl?: string | null
  projectId: string
  threadId: string
}

export function MessageList({ messages, isLoading = false, onScrollToBottom, projectIconUrl, projectId, threadId }: MessageListProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null)

  // Callback to scroll to bottom within the scroll container
  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current && onScrollToBottom) {
      // Find the scroll container (ScrollArea viewport)
      const scrollContainer = messagesEndRef.current.closest('[data-radix-scroll-area-viewport]') as HTMLElement
      if (scrollContainer) {
        onScrollToBottom(scrollContainer)
      }
    }
  }, [onScrollToBottom])

  // Smart auto-scroll: only during streaming and when user is near bottom
  useEffect(() => {
    // Only auto-scroll during active streaming
    if (!isLoading) return
    
    // Check if user is near bottom before auto-scrolling
    if (messagesEndRef.current && onScrollToBottom) {
      const scrollContainer = messagesEndRef.current.closest('[data-radix-scroll-area-viewport]') as HTMLElement
      if (scrollContainer) {
        // Check if user hasn't manually scrolled up (within 100px of bottom)
        const isNearBottom = scrollContainer.scrollTop + scrollContainer.clientHeight >= 
          scrollContainer.scrollHeight - 100
        
        if (isNearBottom) {
          // Small delay to ensure DOM has updated
          const timer = setTimeout(() => onScrollToBottom(scrollContainer), 50)
          return () => clearTimeout(timer)
        }
      }
    }
  }, [messages, isLoading, onScrollToBottom])

  return (
    <div className="space-y-4 py-4 bg-white">
      {messages.map((message) => (
        <MessageRenderer
          key={message.id}
          message={message}
          projectIconUrl={projectIconUrl}
          projectId={projectId}
          threadId={threadId}
        />
      ))}
      
      {isLoading && (
        <MessageRenderer
          message={{
            id: 'loading',
            role: 'assistant',
            content: '',
            timestamp: new Date(),
            parts: []
          }}
          isLoading={true}
          projectIconUrl={projectIconUrl}
          projectId={projectId}
          threadId={threadId}
        />
      )}
      
      {/* Invisible element to mark the end */}
      <div ref={messagesEndRef} />
    </div>
  )
} 